/**
 * Export a resolved table to CSV (UTF-8 + BOM, RFC-4180 quoting) or XLSX
 * (SheetJS — data sheet + metadata sheet). Honors header strategy (label vs
 * field_key) and form-prefix per export spec §15.6. Cell text is the same
 * `cellText` the grid renders, so exports never diverge from the screen.
 */
import { cellText } from './cell-text'
import type { ResolvedColumn, ResolvedRow } from './spec-resolver'

export type HeaderStrategy = 'label' | 'field_key'

function headerFor(col: ResolvedColumn, strategy: HeaderStrategy): string {
  if (strategy === 'label') return col.label
  const base = col.catalogField?.fieldKey ?? col.metaKey ?? col.label
  return col.instanceIndex ? `${base}@${col.instanceIndex}` : base
}

export interface TableMatrix {
  headers: string[]
  matrix: string[][]
}

export function tableToMatrix(
  columns: ResolvedColumn[],
  rows: ResolvedRow[],
  strategy: HeaderStrategy,
): TableMatrix {
  return {
    headers: columns.map((c) => headerFor(c, strategy)),
    matrix: rows.map((r) => columns.map((c) => cellText(c, r))),
  }
}

// ─── CSV ────────────────────────────────────────────────────────────────────

function csvCell(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`
  return v
}

export function toCsv(headers: string[], matrix: string[][]): string {
  const lines = [headers, ...matrix].map((row) => row.map(csvCell).join(','))
  return '﻿' + lines.join('\r\n') // BOM for Excel
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export function downloadCsv(filename: string, headers: string[], matrix: string[][]) {
  triggerDownload(new Blob([toCsv(headers, matrix)], { type: 'text/csv;charset=utf-8' }), filename)
}

// ─── XLSX ─────────────────────────────────────────────────────────────────────

export interface ExportMeta {
  viewName: string
  total: number
  generatedAt: string
  filters: string[]
}

export async function downloadXlsx(
  filename: string,
  data: TableMatrix,
  meta: ExportMeta,
) {
  // Dynamic import keeps SheetJS (~1 MB) out of the main bundle — loaded only on
  // the first Excel export.
  const XLSX = await import('xlsx')
  const wb = XLSX.utils.book_new()

  const metaRows = [
    ['نما', meta.viewName],
    ['تاریخ خروجی', meta.generatedAt],
    ['تعداد ردیف', String(meta.total)],
    ['فیلترها', meta.filters.length ? meta.filters.join(' ؛ ') : '—'],
  ]
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(metaRows), 'اطلاعات')

  const ws = XLSX.utils.aoa_to_sheet([data.headers, ...data.matrix])
  XLSX.utils.book_append_sheet(wb, ws, 'داده‌ها')

  XLSX.writeFile(wb, filename)
}
